import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission, type ModuleScreen } from '~/utils/rbacModuleIndex'

export type UsersScreen = 'app' | 'payment-defaults' | 'payment-links' | 'bo'

/**
 * Source unique consommée par le `staticData.permission` de chaque écran du module et par la
 * redirection `/users` : pas de dérive possible entre l'écran et la garde de sa route.
 */
export const USERS_SCREEN_PERMISSIONS = {
  app: ['read', 'users.app-users.user'],
  'payment-defaults': ['read', 'users.payment-defaults.default'],
  /** Même garde que `GET /payment-links` (bit ADMIN en régime hérité, cf. `customers-permissions`). */
  'payment-links': ['read', 'users.payment-links.link'],
  bo: ['read', 'users.bo-users.user'],
} as const satisfies Record<UsersScreen, PermissionTuple>

/**
 * Écrans du module 7 (Utilisateurs), dans l'ordre de repli de la redirection `/users`.
 *
 * Depuis la refonte Clients (kanban #870), il n'y a plus de barre d'onglets : « Clients »
 * (`/users/app`) mène à « Clients bannis » (`/users/payment-defaults`) par sa tuile et à « Liens de
 * paiement » (`/users/payment-links`, lot 7) par son en-tête, chacun avec son lien de retour. Les
 * comptes du back-office vivent sous Administration (`/administration/access`) ; `/users/bo` ne
 * sert plus que de redirection.
 */
export const USERS_SCREENS: (ModuleScreen<RouteTo> & { value: UsersScreen })[] = [
  { value: 'app', to: '/users/app', permission: USERS_SCREEN_PERMISSIONS.app },
  {
    value: 'payment-defaults',
    to: '/users/payment-defaults',
    permission: USERS_SCREEN_PERMISSIONS['payment-defaults'],
  },
  {
    value: 'payment-links',
    to: '/users/payment-links',
    permission: USERS_SCREEN_PERMISSIONS['payment-links'],
  },
]

/** Accès au module : dès qu'un seul de ses écrans est accessible. */
export const USERS_PAGE_READ: PermissionRequirement = moduleIndexPermission(
  USERS_SCREENS.map((screen) => screen.permission),
)
